import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { commonClassName, commonHeaderClass } from './PromoScorecard';
import { UserContext } from 'contexts/ConfigContext';
import { formatNumWithKMB, formatNumWithKMBAlt, getAdditionalDataOnCopyClick, getConfig } from 'utils/helperMethods';

import { colorConstants } from 'config/colorConstants';

import PriceCurveMultiLineChartContainer, {
    priceCurveMultiLineId,
} from 'components/template/PriceCurve/PriceCurveMultiLineChartContainer';
import PriceCurveBarChartContainer from 'components/template/PriceCurve/PriceCurveBarChartContainer';
import PriceCurveTrendsChartContainer from 'components/template/PriceCurve/PriceCurveTrendsChartContainer';
import { CustomTable } from 'components/molecules/GrowthAnalysis/GrowthAnalysisTable';
import { chartHeight, yAxisWidth } from 'components/template/PriceLadder/BarPointChartContainer';
import { getPriceCurveTableColumns } from 'utils/getPriceCurveTableColumns';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { useFetchMultiLineChartData } from 'hooks/priceCurve/useFetchMultiLineChartData';
import { salesGraphWidth } from 'components/organism/GrowthAnalysis/SalesChartContainer/SalesChartContainer';
import { FilterContextData } from 'contexts/FilterContext';
import _ from 'lodash';
import { useFetchTrendsChart } from 'hooks/priceCurve/useFetchTrendsChart';
import { useFetchBarChart } from 'hooks/priceCurve/useFetchBarChart';
import { useApplyFilterMutation } from 'hooks/general/useApplyFilterMutation';
import { defaultApplyFilter } from './GrowthAnalysis';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { priceCurveTableDownload } from 'parser/graphToExcelDownloadParser';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { priceCurveUrls } from 'config/urls';
import { useDebounceLoading } from 'hooks/general/useDebounceLoading';
import { ConfigKeys } from 'utils/Enums';

const priceCurveMainHeader = 'Price Curve';

const tableId = 'price-curve-table-id';

const PriceCurve = () => {
    const { appConfig } = useContext(UserContext);
    const priceCurveConfig = getConfig('priceCurve', appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);

    const xAxisDropdownValues = priceCurveConfig?.xAxisKeyValue;
    const yAxisDropdownValues = priceCurveConfig?.YAxisKeyValue;
    const levelsDropdownValues = priceCurveConfig?.levels;
    const [subTileState, setSubTileState] = useState(
        priceCurveConfig?.subTileData?.[priceCurveConfig?.subTileData?.length - 1],
    );
    const [xAxisSelectedValue, setXAxisSelectedValue] = useState(xAxisDropdownValues?.[0]);
    const [yAxisSelectedValue, setYAxisSelectedValue] = useState(yAxisDropdownValues?.[0]);
    const [levelsSelectedValue, setLevelsSelectedValue] = useState<any>();
    const [selectedLegends, setSelectedLegends] = useState<any[]>([]);
    const [selectedBarItem, setSelectedBarItem] = useState<any[]>([]);

    const offsetChart1 = useRef(0);
    const chart2ApplyFilterVar = useRef<any>({});
    const chart3ApplyFilterVar = useRef<any>({});
    const tableCopyRef = useRef<any>();
    //CONTEXT
    const {
        allDimAndAttributeTypeList,
        idForRestOfComponent,
        timePeriodLoading,
        timePeriodSelection,
        idForRestOfComponentLoading,
        filterNullError,
        allCheckedAttribute,
        timeSelectionLabel,
    } = useContext(FilterContextData);

    const enabledVariable = Boolean(
        allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !idForRestOfComponentLoading &&
            !timePeriodLoading &&
            idForRestOfComponent,
    );

    const levelStatusVariable = {
        filters: idForRestOfComponent,
        levels_list: levelsDropdownValues?.map((item: any) => item?.value),
        time_period: JSON.parse(timePeriodSelection || '{}'),
    };

    //react query to fetch levels enabled values
    const {
        error: levelsDataError,
        loading: levelsDataLoading,
        data: levelsData,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: enabledVariable },
        queryKey: ['price-curve-levels-enabled', levelStatusVariable],
        url: priceCurveUrls.levelStatus,
        variables: {
            ...levelStatusVariable,
        },
        parserFunction: (data: any) => {
            let updatedLevelsDropdownValues: any = null;
            let defaultLevel: any = null;
            if (data && Array.isArray(data?.levels)) {
                updatedLevelsDropdownValues = levelsDropdownValues.map((item: any) => {
                    const level = data?.levels.find((level: any) => {
                        return level?.name === item?.value;
                    });
                    //set the default value
                    if (!defaultLevel && level?.enabled) {
                        defaultLevel = {
                            ...item,
                            disabled: !level?.enabled,
                        };
                    }
                    //adding disabled to the item
                    return {
                        ...item,
                        disabled: !level?.enabled,
                    };
                });
            }
            setLevelsSelectedValue(defaultLevel ? defaultLevel : levelsDropdownValues?.[0]);
            return updatedLevelsDropdownValues;
        },
    });
    //HOOKS
    const {
        data: multiLineData,
        error: multiLineError,
        loading: multiLineLoading,
        fetchMultiLineChartData,
    } = useFetchMultiLineChartData({ yAxisSelectedValue });

    const {
        data: trendsData,
        loading: trendsDataLoading,
        error: trendsDataError,
        fetchTrendsChartData,
    } = useFetchTrendsChart();

    const {
        data: chart2Data,
        loading: chart2DataLoading,
        error: chart2DataError,
        fetchBarChartData,
    } = useFetchBarChart();

    const {
        data: chart2ApplyFilterData,
        loading: chart2ApplyFilterLoading,
        error: chart2ApplyFilterError,
        applyFilterMutate: chart2ApplyFilterMutate,
    } = useApplyFilterMutation({ onComplete: onChart2ApplyFilterComplete, onError: onChart2ApplyFilterError });

    const {
        data: chart3ApplyFilterData,
        loading: chart3ApplyFilterLoading,
        error: chart3ApplyFilterError,
        applyFilterMutate: chart3ApplyFilterMutate,
    } = useApplyFilterMutation({ onComplete: onChart3ApplyFilterComplete, onError: onChart3ApplyFilterError });

    //graph main axis width
    const containerWidth = useContainerWidth({
        containerId: priceCurveMultiLineId,
        buffer: yAxisWidth * 1 + 34 * 2,
    });

    //graph width config
    const widthConfig = useMemo(() => {
        return {
            containerWidth,
        };
    }, [containerWidth]);

    const memoisedLegendData = useMemo(() => {
        const colorMapping: any = {};
        const chartColors = priceCurveConfig?.chartColors;

        //dynamically allocate colors
        const updatedLegendData = multiLineData?.legends?.map((item: any, index: number) => {
            const color = chartColors[index % chartColors?.length];
            colorMapping[item.key] = color;
            return {
                ...item,
                color,
            };
        });
        return {
            colorMapping,
            legendData: updatedLegendData,
        };
    }, [multiLineData]);

    const memoisedData = useMemo(() => {
        let updatedLegendData: any[] = [];
        let updatedData: any[] = multiLineData?.chartData || [];
        if (selectedLegends?.length > 0) {
            updatedLegendData = memoisedLegendData?.legendData?.filter(
                (item: any) => !selectedLegends.includes(item.label),
            );
            updatedData = updatedData.filter((dataItem) => selectedLegends.includes(dataItem.type));
        }
        // setSelectedBarItem([]);
        return {
            data: updatedData,
            min: multiLineData?.min || 0,
            max: multiLineData?.max || 0,
            xAxisValues: multiLineData?.xAxisValues || [],
            legends: updatedLegendData,
        };
    }, [selectedLegends, multiLineData, memoisedLegendData]);

    const memoisedDropdownList = useMemo(() => {
        const updatedXAxisDropdownValues = xAxisDropdownValues.filter(
            (item: any) => item.value !== levelsSelectedValue?.value,
        );
        const updatedLevelDropdownValues = levelsData?.filter((item: any) => item.value !== xAxisSelectedValue?.value);
        return {
            xAxis: updatedXAxisDropdownValues,
            yAxis: yAxisDropdownValues,
            levels: updatedLevelDropdownValues,
        };
    }, [xAxisSelectedValue, levelsSelectedValue]);

    const memoisedTableData = useMemo(() => {
        let tempData = Array.isArray(multiLineData?.tableData) ? [...multiLineData?.tableData] : [];
        tempData =
            selectedLegends?.length > 0 ? tempData.filter((item) => selectedLegends.includes(item?.xAxis)) : tempData;

        const graphViewContainerWidth =
            Math.max(
                memoisedData?.xAxisValues ? memoisedData?.xAxisValues?.length * salesGraphWidth : 0,
                containerWidth,
            ) || window.innerWidth;
        const perGraphComponentWidth = graphViewContainerWidth / memoisedData?.xAxisValues?.length || 0;
        return {
            data: tempData,
            columns: getPriceCurveTableColumns(
                tempData,
                perGraphComponentWidth,
                levelsSelectedValue?.label,
                decimalForNonPercentage,
                decimalForPercentage,
            ),
        };
    }, [multiLineData?.tableData, selectedLegends]);

    useEffect(() => {
        if (levelsDataError) {
            setLevelsSelectedValue(levelsDropdownValues?.[0]);
        }
    }, [levelsDataError]);

    //listening to scroll
    useEffect(() => {
        //table element
        const tableScrollableElement = document.getElementById(tableId)?.getElementsByClassName('ant-table-body')[0];
        //graph element
        const graphElem = document.getElementById(priceCurveMultiLineId);

        //scroll table when user is scrolling sales chart
        function scrollFunction(event: any) {
            tableScrollableElement?.scrollTo({
                left: Number(graphElem?.scrollLeft) * 1, //giving a buffer because widths of table and chart are different
                behavior: 'auto',
            });
        }
        if (multiLineData?.tableData?.length > 0) {
            graphElem?.addEventListener('scroll', scrollFunction, { passive: true });
            return () => {
                graphElem?.scrollTo({
                    left: 0,
                });
                graphElem?.removeEventListener('scroll', scrollFunction);
            };
        }
    }, [multiLineData]);

    //CHART 2
    useEffect(() => {
        setSelectedBarItem([]);

        chart2ApplyFilterVar.current = {};
        if (selectedLegends?.length) {
            const inclusionItem = selectedLegends.map((item) => {
                return {
                    attributeExId: item,
                    attributeDesc: item,
                    attributeType: _.snakeCase(levelsSelectedValue?.value),
                    dimension: 'dim1',
                };
            });
            prepareApplyFilterVariableAndExecute(inclusionItem, [], chart2ApplyFilterMutate);
        }
    }, [selectedLegends]);

    //CHART 3
    useEffect(() => {
        chart3ApplyFilterVar.current = {};
        if (selectedBarItem?.length) {
            const tempObj = {
                attributeExId: selectedBarItem?.[0]?.category,
                attributeDesc: selectedBarItem?.[0]?.category,
                attributeType: _.snakeCase(xAxisSelectedValue?.value),
                dimension: 'dim1',
            };
            prepareApplyFilterVariableAndExecute([tempObj], [], chart3ApplyFilterMutate);
        }
    }, [selectedBarItem]);

    //chart 1 api call
    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !idForRestOfComponentLoading &&
            !timePeriodLoading &&
            levelsSelectedValue
        ) {
            offsetChart1.current = 0;
            fetchMultiLineChartData({
                variables: {
                    xAxisAttribute: _.snakeCase(xAxisSelectedValue?.value),
                    metric: _.snakeCase(yAxisSelectedValue?.value),
                    levelAttribute: _.snakeCase(levelsSelectedValue?.value),
                    metricType: subTileState?.dataKey,
                    filters: JSON.stringify(idForRestOfComponent),
                    timePeriod: timePeriodSelection?.toLowerCase(),
                    limit: priceCurveConfig?.chart1Limit,
                    offset: offsetChart1.current,
                },
            });
        }
    }, [xAxisSelectedValue, yAxisSelectedValue, levelsSelectedValue, subTileState]);

    useEffect(() => {
        setSelectedLegends([]);
    }, [levelsSelectedValue, xAxisSelectedValue, timePeriodSelection, idForRestOfComponent]);

    //chart 3 and 2 api call
    useEffect(() => {
        if (selectedLegends?.length) {
            parseAndFetchChart3Data();
            parseAndFetchChart2Data();
        }
    }, [subTileState, yAxisSelectedValue]);

    const { loading: debouncedLoading } = useDebounceLoading({
        loadingDependency:
            idForRestOfComponentLoading ||
            timePeriodLoading ||
            allDimAndAttributeTypeList?.loading ||
            levelsDataLoading,
    });
    const commonLoading = multiLineLoading || debouncedLoading;
    const commonLoadingChart2 = chart2ApplyFilterLoading;

    //function which finds intersection between new filter and existing filter
    function getIntersectionOnApplyFilter(filter: any, payload: any) {
        const tempApplyFilterResponse = JSON.parse(JSON.stringify(filter));
        let commonIdsDim1 = [];
        let commonIdsDim2 = [];
        if (payload?.dim1?.ids?.length > 0) {
            commonIdsDim1 = [...payload.dim1.ids];
            if (filter.dim1.ids?.length > 0) {
                //if no id in global filter , it means all are selected
                commonIdsDim1 = _.intersection(filter.dim1.ids, payload.dim1.ids);
            }
            tempApplyFilterResponse.dim1.ids = [...commonIdsDim1];
        }
        if (payload?.dim2?.ids?.length > 0) {
            commonIdsDim2 = [...payload.dim2.ids];
            if (filter.dim2.ids?.length > 0) {
                //if no id in global filter , it means all are selected
                commonIdsDim2 = _.intersection(filter.dim2.ids, payload.dim2.ids);
            }
            tempApplyFilterResponse.dim2.ids = [...commonIdsDim2];
        }
        return tempApplyFilterResponse;
    }
    //CHART 2
    function onChart2ApplyFilterComplete(payload: any) {
        const tempApplyFilterResponse = getIntersectionOnApplyFilter(idForRestOfComponent, payload);
        chart2ApplyFilterVar.current = tempApplyFilterResponse;
        parseAndFetchChart2Data();
    }
    function onChart2ApplyFilterError() {}

    //CHART 3
    function onChart3ApplyFilterComplete(payload: any) {
        const tempApplyFilterResponse = getIntersectionOnApplyFilter(chart2ApplyFilterVar.current, payload);
        chart3ApplyFilterVar.current = tempApplyFilterResponse;
        parseAndFetchChart3Data();
    }
    function onChart3ApplyFilterError() {}

    //function to prepare apply filter object
    function prepareApplyFilterVariableAndExecute(
        dim1Variables: any[],
        dim2Variables: any[],
        applyFilterFunction: Function,
    ) {
        const tempApplyFilterVariable: any = _.cloneDeep(defaultApplyFilter);
        if (dim1Variables?.length) tempApplyFilterVariable.jsonstring_dim1.inclusion = dim1Variables;
        if (dim2Variables?.length) {
            tempApplyFilterVariable.jsonstring_dim2.inclusion = dim2Variables;
        }
        tempApplyFilterVariable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim1);
        tempApplyFilterVariable.jsonstring_dim2 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim2);
        applyFilterFunction({ variables: tempApplyFilterVariable });
    }

    //function to fetch chart 2 data
    function parseAndFetchChart2Data() {
        fetchBarChartData({
            variables: {
                xAxisAttribute: _.snakeCase(xAxisSelectedValue?.value),
                levelAttribute: _.snakeCase(levelsSelectedValue?.value),
                metricType: subTileState?.dataKey,
                metric: _.snakeCase(yAxisSelectedValue?.value),
                filters: JSON.stringify(chart2ApplyFilterVar.current),
                levelAttributeNames: selectedLegends,
                graphName: selectedLegends?.length === 1 ? 'incentive' : 'comparison',
                timePeriod: timePeriodSelection?.toLowerCase(),
                limit: priceCurveConfig?.chart1Limit,
                offset: 0,
            },
        });
    }

    //function to fetch chart 3 data
    function parseAndFetchChart3Data() {
        fetchTrendsChartData({
            variables: {
                xAxisAttributeName: selectedBarItem?.[0]?.category,
                xAxisAttribute: _.snakeCase(xAxisSelectedValue?.value),
                levelAttribute: _.snakeCase(levelsSelectedValue?.value),
                metricType: subTileState?.dataKey,
                metric: _.snakeCase(yAxisSelectedValue?.value),
                graphName: selectedLegends?.length === 1 ? 'incentive' : 'comparison',
                filters: JSON.stringify(chart3ApplyFilterVar.current),
                levelAttributeNames: selectedLegends,
                timePeriod: timePeriodSelection?.toLowerCase(),
                limit: priceCurveConfig?.chart1Limit,
                offset: 0,
            },
        });
    }

    const tableExcelData = useMemo(() => {
        return priceCurveTableDownload(memoisedTableData?.data, levelsSelectedValue?.label);
    }, [memoisedTableData?.data, selectedLegends]);

    return (
        <section className={`${commonClassName} bg-white`}>
            <h2 className={commonHeaderClass}>{priceCurveMainHeader}</h2>
            {/* MULTI LINE CHART */}
            <PriceCurveMultiLineChartContainer
                loading={commonLoading}
                error={multiLineError}
                subTileState={subTileState}
                setSubTileState={setSubTileState}
                widthConfig={widthConfig}
                dropdownList={memoisedDropdownList}
                memoisedData={memoisedData}
                xAxisSelectedValue={xAxisSelectedValue}
                setXAxisSelectedValue={setXAxisSelectedValue}
                setYAxisSelectedValue={setYAxisSelectedValue}
                setLevelsSelectedValue={setLevelsSelectedValue}
                yAxisSelectedValue={yAxisSelectedValue}
                levelsSelectedValue={levelsSelectedValue}
                legendData={memoisedLegendData}
                selectedLegends={selectedLegends}
                setSelectedLegends={setSelectedLegends}
            />
            {/* BAR CHART */}
            {selectedLegends?.length > 0 && (
                <PriceCurveBarChartContainer
                    data={chart2Data}
                    loading={chart2DataLoading || commonLoadingChart2}
                    error={chart2DataError || chart2ApplyFilterError}
                    widthConfig={widthConfig}
                    selectedBarItem={selectedBarItem}
                    setSelectedBarItem={setSelectedBarItem}
                    selectedLegends={selectedLegends}
                    xAxisSelectedValue={xAxisSelectedValue}
                    yAxisSelectedValue={yAxisSelectedValue}
                    levelsSelectedValue={levelsSelectedValue}
                />
            )}
            {/* TRENDS CHART */}
            {selectedBarItem?.length > 0 && (
                <PriceCurveTrendsChartContainer
                    widthConfig={widthConfig}
                    selectedBarItem={selectedBarItem}
                    data={trendsData}
                    error={trendsDataError || chart3ApplyFilterError}
                    loading={trendsDataLoading || chart3ApplyFilterLoading}
                    selectedLegends={selectedLegends}
                    xAxisSelectedValue={xAxisSelectedValue}
                    yAxisSelectedValue={yAxisSelectedValue}
                    levelsSelectedValue={levelsSelectedValue}
                />
            )}
            {/* TABLE */}

            <div id={tableId} ref={tableCopyRef} className="mt-4">
                <div className="flex justify-end gap-2 items-center mb-4">
                    {!commonLoading && (
                        <>
                            <CopyToClipBoard refOfComponent={tableCopyRef} />
                            <DownloadExcel
                                title={'PriceCurveTable'}
                                excelData={tableExcelData.data}
                                headers={tableExcelData.headers}
                            />
                        </>
                    )}
                </div>
                <CustomTable
                    bordered={false}
                    pagination={false}
                    scroll={{ x: '100%', y: chartHeight + 200 }}
                    sticky
                    // style={style}
                    // rowKey={(record: any) => record[rowKey]}
                    columns={memoisedTableData?.columns}
                    dataSource={memoisedTableData?.data || []}
                    loading={commonLoading}
                />
            </div>
        </section>
    );
};

export default PriceCurve;
